import * as React from 'react';
import * as Theme from '@sail/theme-classic';
import type {IconAsset, SvgType} from '@sail/icons/types';

import {view} from '../view';
import {css} from '../css';

type IconSet = 'Icon';
type IconSize = 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

export interface IconProps {
  /**
   * @external
   */
  'aria-hidden'?: boolean;
  icon: IconAsset;
  /**
   * @external
   */
  size?: IconSize;
}

const colorableStyles = css({
  fill: css.value('currentColor'),
});

const uncolorableStyles = css({
  // Colorable icons get currentColor, but icons that
  // aren't colorable should have this default set (often redundant)
  fill: css.value('#000'),
});

const caches = new Map<string, WeakMap<IconAsset, React.FC>>();

// Small is already the default, so nothing needs to fall back to it
const SIZE_FALLBACKS: Partial<Record<IconSize, IconSize[]>> = {
  xxsmall: ['xsmall'],
  large: ['medium'],
  xlarge: ['large', 'medium'],
};

function getIconSizeName(iconSet: IconSet | null, size?: IconSize) {
  return [iconSet, size].filter(Boolean).join('__') || '';
}

function getClosestSizeIcon(
  alt: Record<string, SvgType>,
  iconSet: IconSet | null,
  size?: IconSize,
) {
  // Grab the name for the specific icon size
  const sizeName = getIconSizeName(iconSet, size);

  // If we get a valid icon name back, and it exists in the alt set, return it
  if (sizeName && alt[sizeName]) {
    return alt[sizeName];
  }

  // If we do pass a size, but it didn't directly match, try to find a fallback
  if (size) {
    // Look up the valid fallbacks for the provided size
    const fallbacks = SIZE_FALLBACKS[size];

    if (fallbacks) {
      // For each fallback in order
      for (let i = 0; i < fallbacks.length; i++) {
        // See if we have a specific icon in *that* size
        const fallbackSizeName = getIconSizeName(iconSet, fallbacks[i]);
        // If the fallback size name exists in the alt set, return it
        if (fallbackSizeName && alt[fallbackSizeName]) {
          return alt[fallbackSizeName];
        }
      }
    }
  }

  // If after all that we don't have an icon that directly matched, just return the default icon set, unsized
  return alt[iconSet || ''];
}

interface EngineIconProps {
  size?: IconSize;
}

const getEngineIcon = (
  iconAsset: IconAsset,
  preferredIconSet: IconSet | null,
  size?: IconSize,
): React.FC => {
  // try to load the icon from the preferred icon set with the size appended
  // if there is no preferred icon set, this will just check for the size
  const key = getIconSizeName(preferredIconSet, size);
  const cache = caches.get(key) || caches.set(key, new WeakMap()).get(key)!;
  const cachedIcon = cache.get(iconAsset);

  if (cachedIcon) {
    return cachedIcon;
  } else {
    let SvgElem = iconAsset.svg;

    if (iconAsset.alt) {
      const alt = getClosestSizeIcon(iconAsset.alt, preferredIconSet, size);

      if (alt) {
        SvgElem = alt;
      }
    }

    const EngineIcon = view.svg.as(SvgElem).extend<EngineIconProps>(
      function EngineIcon({size, ...innerprops}, View) {
        return (
          <View
            uses={[
              iconAsset.colorable ? colorableStyles : uncolorableStyles,
              Theme.Icon,
              Theme.IconVariants.assign({size}),
              innerprops,
            ]}
          />
        );
      },
      [Theme.IconVariants.internal_defaults],
    );

    cache.set(iconAsset, EngineIcon);

    return EngineIcon;
  }
};

/**
 * @external
 */
export const Icon = view
  .create<IconProps, unknown, 'svg', typeof css.ComputedProperties>(
    function Icon({icon, 'aria-hidden': ariaHidden = true, ...props}) {
      const EngineIcon = getEngineIcon(icon, 'Icon', props.size);
      return <EngineIcon {...props} aria-hidden={ariaHidden} />;
    },
  )
  .styles(css);
